
import { Component, Prop, Vue } from 'vue-property-decorator';
import { TimeSlotSelection } from '@/types/time-slots';
import { LocationData, LocationPrice } from '@/types/locations';
import { Discount, PromotionType } from '@/types/promotions';
import Divider from '@/ui-components/Divider/Divider.vue';
import LocationCityAndAddress from '@/shared-components/LocationCityAndAddress.vue';
import SkeletonLoader from '@/ui-components/SkeletonLoader/SkeletonLoader.vue';
import {
  addVatTo,
  calcVatOn,
  toNumberOr,
  VAT_PERCENTAGE,
} from '@/utils/numbers';
import { chain } from 'lodash';
import { CampaignFrequency } from '@/types/campaigns';
import {getDuration, isAllSelectedLocationsPriceControlByShasha} from '@/utils/campaigns';
import namespaces from "@/store/namespaces";
import { Getter } from 'vuex-class';
import moment from 'moment';

@Component({
  components: {
    Divider,
    LocationCityAndAddress,
    SkeletonLoader,
  },
})
export default class PriceSummary extends Vue {
  @Prop({ required: true, type: Array, default: () => [] })
  selectedLocations!: LocationData[];
  @Prop({ required: true, type: Object }) timeSlotSelection!: TimeSlotSelection;
  @Prop({ type: Array, default: () => [] }) discounts!: Discount[];
  @Prop({ type: Object, required: true }) locationPrices!: Record<LocationData['ID'], LocationPrice>;
  @Prop({ }) closeSheet!:Function;
  @Prop({ type: Boolean, default: false }) isLoadingDiscounts!: boolean;

  @Getter('time', { namespace: namespaces.CampaignWizardModule })
  public campaignWizardSelectedTimeSlots!: TimeSlotSelection;

  get discountsMap() {
    return this.discounts.reduce((discounts, discount) => ({
      ...discounts,
      [discount?.screenClusterId || '']: discount
    }), {}) as Record<LocationData['ID'], Discount>
  };

  get isAllSelectedLocationsPriceControlByShasha() {
    return isAllSelectedLocationsPriceControlByShasha(this.selectedLocations)
  }
  get priceSummaryTitle(){
    return this.isAllSelectedLocationsPriceControlByShasha? this.$t('price_summary'): this.$t('budgetary_summary');
  }
  public get frequencyName() {
    const { NAME = '' } = this.timeSlotSelection.frequency || {} as CampaignFrequency;
    return this.$t(NAME);
  }

  public get subTotalCampaignPrice() {
    const {
      duration = 0,
    } = this.timeSlotSelection;
    const selectedDuration = toNumberOr(duration, 0);

    const subtotal = this.selectedLocations.reduce(
      (total, location) => {
        const locationPrice = this.locationPrices?.[location.clusterId]?.BASE_PRICE;
        return total + selectedDuration * locationPrice
      },
      0
    );
    return subtotal;
  }

  public get totalCampaignDiscounts() {
    return chain(this.discounts)
      .map(({ discountValue }) => discountValue)
      .sum()
      .value();
  }

  public get subTotalCampaignPriceAfterDiscount() {
    return this.subTotalCampaignPrice - this.totalCampaignDiscounts;
  }

  public get totalCampaignPrice() {
    const totalAfterVat = addVatTo(this.subTotalCampaignPriceAfterDiscount);
    return totalAfterVat;
  }

  public discountInfo({ discountType = {} as PromotionType }: Discount) {
    const { RATE, VALUE } = discountType;
    const promotionType = RATE === 'DISCOUNT' ? `%` : ` <span class="icon-saudi_riyal">ê</span>`;
    return `${VALUE}${promotionType} ${this.$t('off')}`;
  }

  public discountValue(discount: Discount) {
    return discount.discountValue;
  }

  public get vatValue() {
    return calcVatOn(this.subTotalCampaignPriceAfterDiscount);
  }

  public get vatInfo() {
    return `+${VAT_PERCENTAGE}% ${this.$t('vat')}`;
  }

  public calculateLocationPrice(location: LocationData) {
    const {
      duration,
    } = this.timeSlotSelection;
    const selectedDuration = toNumberOr(duration, 0);
    const locationPrice = Number(this.locationPrices?.[location.clusterId]?.BASE_PRICE);
    return locationPrice * selectedDuration;
  }
  get hasCampaignDuration(){
    const {startDate, endDate, duration} = this.campaignWizardSelectedTimeSlots;
    return (startDate && endDate) || (duration);
  }

  get campaignDuration() {
    const { startDate, endDate, duration } = this.campaignWizardSelectedTimeSlots;
    if (startDate && endDate) {
      return getDuration(new Date(startDate), new Date(endDate))
    }
    if (duration) {
      const endDateFromDuration = moment(new Date()).add("weeks", duration).toDate();
      return getDuration(new Date(), endDateFromDuration)
    }
  }
}
